import axios from "axios";
import { BASE_CHAT_URL, token } from "./index";

/**
 * Axios instance configured for chat-related API requests.
 */
const axiosChatInstance = axios.create({
  baseURL: BASE_CHAT_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
});

/**
 * ChatApiService - A service for handling chat-related HTTP requests.
 *
 * Provides basic methods (GET, POST, POST with FormData) for interacting with the chat API.
 *
 * @module ChatApiService
 */
export const ChatApiService = {
   /**
   * Sends a GET request to the chat API.
   *
   * @param {string} url - The endpoint URL.
   * @param {object} [params] - Optional query parameters.
   * @returns {Promise<any>} A promise that resolves to the response data from the chat API.
   */
  get: async (url: string, params?: object) => {
    try {
      const response = await axiosChatInstance.get(url, { ...params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

   /**
   * Sends a POST request to the chat API with JSON payload.
   *
   * @param {string} url - The endpoint URL.
   * @param {object} [data] - Optional data to send in the request body.
   * @returns {Promise<any>} A promise that resolves to the response data from the chat API.
   */
  post: async (url: string, data?: object) => {
    try {
      const response = await axiosChatInstance.post(url, data, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Sends a POST request to the chat API with FormData (for file uploads).
   *
   * @param {string} url - The endpoint URL.
   * @param {FormData} formData - The FormData object to send.
   * @returns {Promise<any>} A promise that resolves to the response data from the chat API.
   */
  postFormData: async (url: string, formData: FormData) => {
    try {
      const response = await axiosChatInstance.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};
